import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import PageLoader from "components/common/PageLoader";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import bankDetailSchema from "./schemas/bank.schema";
import { getBank, getCompany, updateBank } from "./services/SettingsService";
import type { BankDetailRow, BankFormValues } from "./types/settings.types";
import { bankToFormValues } from "./utils/settingsNormalize";
import { toastSettingsError } from "./utils/settingsToast";

const BankTab = () => {
  const { can } = useAccess();
  const canUpdate = can(Permissions.SETTINGS.UPDATE);
  const [loading, setLoading] = useState(true);
  const [hasCompany, setHasCompany] = useState(false);
  const [bankRow, setBankRow] = useState<BankDetailRow | null>(null);

  const formik = useFormik<BankFormValues>({
    enableReinitialize: true,
    initialValues: bankToFormValues(bankRow),
    validationSchema: bankDetailSchema,
    onSubmit: async (values) => {
      if (!canUpdate || !hasCompany) return;
      try {
        const res = await updateBank({
          accountName: values.accountName.trim(),
          accountNumber: values.accountNumber.trim(),
          ifscCode: values.ifscCode.trim(),
          bankName: values.bankName.trim(),
          branch: values.branch.trim(),
        });
        if (res.success) {
          toast.success(res.message || "Bank details saved");
          const row = await getBank();
          setBankRow(row);
        } else {
          toast.error(res.message || "Could not save bank details");
        }
      } catch (err) {
        toastSettingsError(err, "Could not save bank details");
      }
    },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const company = await getCompany();
      const ok = company != null;
      setHasCompany(ok);
      if (ok) {
        const row = await getBank();
        setBankRow(row);
      } else {
        setBankRow(null);
      }
    } catch {
      toast.error("Could not load bank details");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <PageLoader message="Loading bank details…" />;
  }

  return (
    <Form onSubmit={formik.handleSubmit}>
      {!hasCompany ? (
        <Alert color="warning" className="mb-3">
          Save a <strong>company profile</strong> first. Bank details are stored
          against the primary company.
        </Alert>
      ) : (
        <p className="text-muted small mb-3">
          Bank account used on invoices and payouts for this organization.
        </p>
      )}
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="bk-acct-name" className="form-label">
            Account name <span className="text-danger">*</span>
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="bk-acct-name"
            name="accountName"
            value={formik.values.accountName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={
              !!(formik.touched.accountName && formik.errors.accountName)
            }
            disabled={!canUpdate || !hasCompany}
          />
          {formik.touched.accountName && formik.errors.accountName ? (
            <FormFeedback type="invalid">
              {formik.errors.accountName}
            </FormFeedback>
          ) : null}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="bk-acct-num" className="form-label">
            Account number <span className="text-danger">*</span>
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="bk-acct-num"
            name="accountNumber"
            value={formik.values.accountNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={
              !!(formik.touched.accountNumber && formik.errors.accountNumber)
            }
            disabled={!canUpdate || !hasCompany}
          />
          {formik.touched.accountNumber && formik.errors.accountNumber ? (
            <FormFeedback type="invalid">
              {formik.errors.accountNumber}
            </FormFeedback>
          ) : null}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="bk-ifsc" className="form-label">
            IFSC code <span className="text-danger">*</span>
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="bk-ifsc"
            name="ifscCode"
            value={formik.values.ifscCode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.ifscCode && formik.errors.ifscCode)}
            disabled={!canUpdate || !hasCompany}
          />
          {formik.touched.ifscCode && formik.errors.ifscCode ? (
            <FormFeedback type="invalid">{formik.errors.ifscCode}</FormFeedback>
          ) : null}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="bk-bank" className="form-label">
            Bank name <span className="text-danger">*</span>
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="bk-bank"
            name="bankName"
            value={formik.values.bankName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.bankName && formik.errors.bankName)}
            disabled={!canUpdate || !hasCompany}
          />
          {formik.touched.bankName && formik.errors.bankName ? (
            <FormFeedback type="invalid">{formik.errors.bankName}</FormFeedback>
          ) : null}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="bk-branch" className="form-label">
            Branch <span className="text-danger">*</span>
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="bk-branch"
            name="branch"
            value={formik.values.branch}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.branch && formik.errors.branch)}
            disabled={!canUpdate || !hasCompany}
          />
          {formik.touched.branch && formik.errors.branch ? (
            <FormFeedback type="invalid">{formik.errors.branch}</FormFeedback>
          ) : null}
        </Col>
      </Row>
      {!canUpdate ? (
        <Alert color="light" className="border mb-3">
          You do not have permission to update settings.
        </Alert>
      ) : null}
      <div className="hstack gap-2 justify-content-end">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!canUpdate || !hasCompany || formik.isSubmitting}
        >
          {formik.isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              />
              Saving…
            </>
          ) : (
            "Save bank details"
          )}
        </button>
      </div>
    </Form>
  );
};

export default BankTab;
